import React, { act, useMemo } from "react";
import { useSelector } from "react-redux";
import { acl } from "./userAccessConstant";
import helperMethods from "./helperMethods";

// Define types for scopes
type Scope = string;

interface UserProfile {
  scopes?: Scope[];
  isAdmin?: boolean;
}

interface Permissions {
  [key: string]: {
    canRead?: boolean;
    canWrite?: boolean;
    canUpdate?: boolean;
    canDelete?: boolean;
  };
}

// Validator function to process user scopes
const scopesValidator = (
  scopes: Scope[] = [],
  isAdmin: boolean = false
): Permissions => {
  let allScopes: any = scopes;

  const isUserManagemnt = helperMethods.isUserManagemnt();

  // If user is admin, all scopes are allowed
  if (isAdmin) {
    allScopes = acl;
    let adminScopes: any = {};

    const getAdminAccess = (module: string) =>{
      let access: boolean = false;
      if(isUserManagemnt){
        access = ["user_management","centres"].includes(module) ? true : false;
      }else{
        access = true;
      }
      return {
        canRead: access,
        canWrite: access,
        canUpdate: access,
        canDelete: access,
      };
    }

    for (const key in allScopes) {
      if (typeof allScopes[key] === "string") {
        adminScopes[key] = getAdminAccess(key);
      } else if (typeof allScopes[key] === "object") {
        if (!adminScopes[key]) {
          adminScopes[key] = {
            ...adminScopes[key],
            [key]: getAdminAccess(key),
          };
        }

        for (const subKey in allScopes[key]) {
          adminScopes[key][subKey] = getAdminAccess(key);
        }
      }
    }
    return adminScopes;
  }

  // Process user scopes for non-admin users
  const finalAccess = allScopes.reduce((permissions: any, scope: any) => {
    if (!scope) return permissions;
    const [portalType, key, subKey, action] = scope.split(":");
    if (!key || !action) return permissions;
    if (!permissions[key]) {
      permissions[key] = {};
    }

    if (!subKey) {
      permissions[key] = {
        module: {
          ...permissions[key]?.module,
          [action]: true
        },
      };
    } else {
      if (!permissions[key][subKey]) {
        permissions[key][subKey] = {};
      }
      permissions[key][subKey][action] = true;
    }

    return permissions;
  }, {});
  return finalAccess;
};

// Custom hook for managing permissions
const usePermission = () => {
  const { scopes = [], isAdmin }: UserProfile = useSelector(
    (state: any) => state?.user || {}
  );
  const permissions: any = useMemo(
    () => scopesValidator(scopes, isAdmin),
    [scopes, isAdmin]
  );

  return { permissions, isAdmin };
};

export default usePermission;
